import React from "react";

export default function SeoGuideSection() {
  return (
    <section className="stf-section">
      <div className="stf-wrap">
        <div className="stf-seo-grid">
          <article className="stf-article">
            <div className="stf-kicker">Guide</div>
            <h2>Hiring event security in Australia: what to know before you book</h2>
            <p>
              Event security is one of the few parts of running an event that carries a legal requirement attached to it. Whether you're putting on a 60-person engagement party or a three-day festival, the rules around who can work the door, how many guards you need and what they're allowed to do are set by your state — not by the venue and not by the security company quoting you.
            </p>
            <p>
              This guide covers what event organisers most often get wrong when hiring crowd controllers, and what to check before you confirm anyone.
            </p>

            <h3 id="licence">Do you legally need a licensed crowd controller?</h3>
            <p>
              In most cases, yes. If someone is screening patrons, checking IDs, monitoring capacity or asking people to leave, that's crowd control work, and every Australian state requires a specific license for it. A general security officer license usually isn't enough on its own.
            </p>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>License commonly required</th>
                  <th>Regulator</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>NSW</td>
                  <td>Security License Class 1C (crowd control)</td>
                  <td>NSW Police SLED</td>
                </tr>
                <tr>
                  <td>VIC</td>
                  <td>Crowd Controller registration</td>
                  <td>Victoria Police LRD</td>
                </tr>
                <tr>
                  <td>QLD</td>
                  <td>Crowd Controller license</td>
                  <td>Office of Fair Trading</td>
                </tr>
                <tr>
                  <td>WA</td>
                  <td>Crowd Controller license</td>
                  <td>WA Police Licensing</td>
                </tr>
                <tr>
                  <td>SA</td>
                  <td>Security agent license (crowd control)</td>
                  <td>Consumer and Business Services</td>
                </tr>
              </tbody>
            </table>
            <div className="stf-callout">
              <b>Worth knowing:</b> license conditions and category names change from time to time, and events serving alcohol often carry extra conditions. Confirm current requirements with your state regulator or your venue's liquor license before you finalise numbers.
            </div>

            <h3 id="how-many">How many guards does your event need?</h3>
            <p>
              There's no single national ratio, and venues, councils and liquor licenses often set their own conditions. As a starting point, most organisers work from patron numbers and then adjust for risk:
            </p>
            <ul>
              <li><strong>Guest numbers</strong> — a common baseline is one crowd controller per 100 patrons, tightening as numbers climb.</li>
              <li><strong>Alcohol</strong> — licensed events almost always need more guards than dry ones, and often RSA-trained ones.</li>
              <li><strong>Entry points</strong> — every gate, bar and backstage door that needs monitoring is its own position.</li>
              <li><strong>Hours and finish time</strong> — late finishes and public transport egress usually need extra cover at the end of the night.</li>
              <li><strong>Event type</strong> — ticketed public events carry different risk to private functions with a known guest list.</li>
            </ul>
            <p>If your venue or council has already set a condition, that number is the floor, not the target.</p>

            <h3 id="cost">What event security costs — and what changes the price</h3>
            <p>
              Rates for licensed crowd controllers in Australia generally sit between $38 and $55 an hour, depending on the work. The biggest factors are the time of day, the length of the shift, the day of the week, and how specialised the role is. Overnight, public holiday and last-minute shifts sit at the top of that range.
            </p>
            <p>
              The other variable is who you book through. Agencies typically add a margin on top of the guard's rate for coordination and rostering. On Staffoo, guards set their own rate and you see it on their application, so the number you compare is the number you pay. You can see current averages in the <a className="inline" href="#pricing">pricing section above</a>.
            </p>

            <h3 id="vs">Crowd controller or security officer — which do you need?</h3>
            <p>These are different licenses covering different work, and booking the wrong one can leave you non-compliant on the night.</p>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Crowd controller</th>
                  <th>Security officer (1A)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Typical work</td>
                  <td>Door, entry screening, crowd monitoring, removals</td>
                  <td>Static site guarding, patrols, access control</td>
                </tr>
                <tr>
                  <td>Best for</td>
                  <td>Festivals, venues, parties, licensed events</td>
                  <td>Offices, construction sites, warehouses, retail</td>
                </tr>
                <tr>
                  <td>Alcohol involved</td>
                  <td>Usually required, often with RSA</td>
                  <td>Not usually sufficient on its own</td>
                </tr>
              </tbody>
            </table>

            <h3 id="checklist">What to check before you confirm a guard</h3>
            <ul>
              <li><strong>License class and expiry</strong> — that it matches the work and is current in your state.</li>
              <li><strong>Relevant experience</strong> — festival work and private function work are different jobs.</li>
              <li><strong>Reviews from similar events</strong> — volume matters less than whether the events looked like yours.</li>
              <li><strong>Availability across the full bump-in and bump-out</strong>, not just the event window.</li>
              <li><strong>Who's responsible for insurance</strong> and what happens if someone can't make the shift.</li>
            </ul>
            <p>On Staffoo, license verification happens before a guard can apply, and the license class, experience and review history sit on every application you receive.</p>

            <h3 id="direct">Hiring direct instead of through an agency</h3>
            <p>
              Traditional agencies take your brief, roster guards internally and send you an invoice — you rarely know who's coming until the day, and you can't request the same person twice. Hiring direct through a platform flips that: you see who applied, you pick, and you can rebook the guards who worked out well. For organisers running recurring events, that continuity usually matters more than the hourly rate.
            </p>
            <p>
              If you're hiring for a different setting, the same process applies to <a className="inline" href="#">corporate and office security</a>, <a className="inline" href="#">retail security</a> and <a className="inline" href="#">construction site security</a>.
            </p>

            <div className="stf-recap">
              <div className="stf-kicker">Before you post</div>
              <p>
                Confirm the license class your state requires, size your headcount from patron numbers rather than a guess, and treat agency quotes as a ceiling — Staffoo shows you each guard's real rate on their application, before you confirm anyone.
              </p>
            </div>
          </article>

          {/* Sidebar Cards Column */}
          <aside className="stf-seo-aside">
            <div className="stf-toc">
              <h4>On this page</h4>
              <a href="#license">Do you need a licensed guard?</a>
              <a href="#how-many">How many guards you need</a>
              <a href="#cost">What it costs</a>
              <a href="#vs">Crowd controller vs officer</a>
              <a href="#checklist">Pre-booking checklist</a>
              <a href="#direct">Direct vs agency</a>
            </div>

            <div className="stf-side-cta">
              <h4>Know what you need?</h4>
              <p>Post the job free and start receiving applications from licensed crowd controllers near you.</p>
              <a href="#" className="stf-btn stf-btn-solid stf-btn-block">Post an event security job</a>
            </div>

            <div className="stf-aside-module">
              <h4>Related guides</h4>
              <a href="#">Licensing explained by state</a>
              <a href="#">How pricing works</a>
              <a href="#">Hiring for a business or agency</a>
              <a href="#">Corporate &amp; office security</a>
            </div>

            <div className="stf-aside-trust">
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Every applicant's license is checked before you see their profile
              </div>
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                </svg>
                Free to post, no lock-in contract, pay after sign-off
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
